'use client';

import {
  Button,
  Popover,
  PopoverHandler,
  PopoverContent,
} from '@material-tailwind/react';
import staticText from 'car-insurance/assets/static-content/dashboard.json';
import { useCarInsuranceContext } from 'car-insurance/context';
import { Sort } from 'car-insurance/types';
import { getSortOrder } from 'car-insurance/utils/getSortOrder';
import classNames from 'classnames';
import { useLocaleSpecificClient } from 'hooks/useSpecificClient';
import React, { useCallback, useMemo, useState } from 'react';
import { track } from 'utils/tracking';
import { Icon } from './Icon';
import { useClientContext } from '$/modules/context/client';

export const SortPopover: React.FC = () => {
  const [isSortPopoverOpen, setSortPopoverOpen] = useState(false);
  const { brand } = useClientContext();

  const price = useLocaleSpecificClient({
    ...staticText.sort.price,
  });
  const recommended = useLocaleSpecificClient({
    ...staticText.sort.recommended,
  });

  const recommendedDesc = useLocaleSpecificClient({
    ...staticText.sort.recommended.description,
  });

  const discount = useLocaleSpecificClient({
    ...staticText.sort.highestDiscount,
  });

  const deductible = useLocaleSpecificClient({
    ...staticText.sort.deductible,
  });

  const sortBy = useLocaleSpecificClient({ ...staticText.sort.sortBy });

  const {
    filters: { sort, providers },
  } = useCarInsuranceContext();

  const sortOptions =
    brand === 'moneymax'
      ? [
          {
            title: price,
            type: Sort.Price,
          },
          {
            title: deductible,
            type: Sort.Deductible,
          },
        ]
      : [
          {
            title: price,
            type: Sort.Price,
          },
          {
            title: recommended,
            description: recommendedDesc,
            type: Sort.Recommended,
          },
          {
            title: discount,
            type: Sort.Discount,
          },
        ];

  const options = useMemo(
    () =>
      brand === 'moneymax'
        ? [staticText.sort.price, staticText.sort.deductible]
        : [
            staticText.sort.price,
            staticText.sort.recommended,
            staticText.sort.highestDiscount,
          ],
    [brand],
  );

  const findCurrentSort = useCallback(() => {
    const currentSort = options.find((item) => {
      if (sort.value === 'Discount') {
        return item._.includes(sort.value.toLowerCase());
      }
      return item._ === sort.value;
    });
    if (currentSort) {
      return currentSort;
    }
    return staticText.sort.recommended;
  }, [options, sort.value]);

  const currentSortValue = useMemo(() => findCurrentSort(), [findCurrentSort]);

  const localizedSort = useLocaleSpecificClient({ ...currentSortValue });

  const handleSortSelect = (value: Sort) => () => {
    sort.setValue(value);
    handleSortPopoverTracking(value);
    setSortPopoverOpen(false);
  };

  const handleSortPopoverTracking = (value: Sort) => {
    const formattedFilterValue = `${value.toLowerCase()}.${getSortOrder(value)}`;
    const trackingData = {
      event: 'filter_action',
      component_type: 'dropdown/drawer',
      component_title: 'Sort',
      action: 'select',
      filter_key: 'sort',
      filter_value: formattedFilterValue,
      filter_applied: {
        sort: formattedFilterValue,
        providers: providers.value,
      },
    };
    track(trackingData);
  };

  return (
    <Popover
      placement="bottom-end"
      open={isSortPopoverOpen}
      handler={setSortPopoverOpen}
    >
      <PopoverHandler>
        <Button
          type="button"
          variant="outlined"
          className="p-button/size text-gray-600 border border-gray-600 flex items-center gap-2 normal-case hover:bg-gray-900 hover:text-white transition-colors rounded-xl"
        >
          <p className="alpha-button-1">
            {brand === 'moneymax' ? '' : `${sortBy} `}
            {localizedSort}
          </p>
          <Icon name="swap_vert" className="hover:text-white" />
        </Button>
      </PopoverHandler>
      <PopoverContent className="p-0 rounded-xl overflow-hidden">
        <ul>
          {sortOptions.map((option) => {
            const selectedSortItem = option.type === sort.value;

            return (
              <li
                className={classNames('min-w-80 hover:bg-primary-100', {
                  'bg-primary-50': selectedSortItem,
                })}
                key={option.title}
              >
                <button
                  onClick={handleSortSelect(option.type)}
                  type="button"
                  className="flex py-2.5 px-4 items-center justify-between w-full"
                >
                  <div className="flex flex-col items-start">
                    <p
                      className={classNames(
                        'text-base font-semibold text-gray-900',
                        {
                          'text-primary-800': selectedSortItem,
                        },
                      )}
                    >
                      {option.title}
                    </p>
                    {option.description && (
                      <p className="text-sm font-medium text-gray-700">
                        {option.description}
                      </p>
                    )}
                  </div>
                  {selectedSortItem && (
                    <Icon name="check" className="text-primary-800" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
